import React, { useState, useCallback, useImperativeHandle, forwardRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";

import ActionsModal from "_components/molecules/Modals/actions-modal";
import JobConfirmation from "../job-components/job-confirmation";
import AddTaskView from "./add-task-view";
import EditTimelineBooking from "./edit-timeline-booking";
import ViewJob from "../job-components/view-job";
import DeleteBooking from "./delete-booking";
import CreateBreakTask from "./create-break-task";
import CreateAdhocTask from "./create-adhoc-task";
import FlightList from "../job-components/flight-list";
import ViewFlight from "../job-components/view-flight";
import ViewPassenger from "../job-components/view-passenger";
import EmployeeChat from "../employee-components/employee-chat";
import FlightChat from "../job-components/flightChat";
import EmployeeSettings from "../employee-components/employee-settings";
import UpdatePaxLocationModal from "../job-components/update-location-modal";
import JobActionMenu from "../job-components/job-action-menu";
import FlightActionMenu from "../job-components/flight-action-menu";
import LocationContextMenu from "../job-components/location-context-menu";

import {
  selectShowConfirmationModal,
  setViewTask,
  selectViewTask,
  reset as resetSelectedTask,
  resetBreakTaskFlags,
  selectShowBreakTaskModal,
  selectShowAdhocTaskModal,
  resetAdhocTaskFlags,
  setSelectedFlightToView,
  selectShowEditModal,
  selectEditModalType,
  setShowEditTaskModal,
  selectSelectedAdhocTask,
  selectSelectedTaskToView,
  selectSelectedFlightToView
} from "_redux/slices/timeline/selected-task";
import {
  selectMenuContext,
  selectMenuPoints,
  openPaxCheckInModal,
  selectPaxLocation,
  selectPaxCheckInModal,
  reset as resetAllMenuContext,
  selectShowDeleteBookingModal,
  selectShowFlightList,
  setShowFlightList,
  selectFlightContext,
  selectFlightContextPoints,
  selectPaxMenuContextModal,
  selectPaxMenuContext,
  selectPaxMenuLocationPoints,
  selectChangeLocModal,
  setChangeLocModal
} from "_redux/slices/utils/menu-context";
import {
  selectSelectedForChat,
  setSelectedForChat,
  setEmployeeMessages,
  selectOpenBroadcastMessages,
  setOpenBroadcastMessages
} from "_redux/slices/timeline/employees";
import {
  selectSelectedFlightToChat,
  setSelectedFlightToChat,
  resetPassengerFlightList
} from '_redux/slices/timeline/flight-information-list';
import { selectInternalNotify, selectShowToast, resetInternalNotify, markInternalNotifySeen } from "_redux/slices/timeline/timeline-toast";
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import {
  selectPassengerModal,
  setPassengerModal,
  setSelectedViewPassenger,
  selectFlightModal,
  setFlightModal,
} from "_redux/slices/timeline/timeline-control";
import { selectBookingType, selectSelectedTimeline } from "_redux/slices/timeline/timeline-areas";
import { resetTimelineToCache, selectShowAddPassenger, setShowAddPassenger, resetAdditionalPassengers as resetAdditionalPassengersFormFlags } from "_redux/slices/timeline/timeline-data";
import {
  selectAddFlightPassengerStatus,
  addFlightPassenger,
  resetAddFlightPassenger
} from "_redux/slices/timeline/timeline-bookings";
import { CustomToast } from "_atoms";
import { formatCarrierCodeAndFlightNumber } from "_utils/functions";
import { selectAdditionalPassengers, resetAdditionalPassengers } from "_redux/slices/prm-bookings/bookings";
import AdditionalPassengerForm from "../job-components/additional-passenger-form";
import PossibleDuplicateWarning from '_components/molecules/assistance-bookings/possible-duplicate-warning';
import { selectIata } from "_redux/slices/main-screen/user";
import BroadcastMessage from "../employee-components/broadcast-message";
import InternalNofification from "_components/atoms/container/internal-notification";

const MenuContextContainer = styled.div`
  border-radius: 4px;
  padding: 18px;
  margin: 5px 0;
  box-sizing: border-box;
  z-index: 9999;
  min-width: 250px;
  min-height: 100px;
`;

const TimelineModals = React.memo(forwardRef(({
  clickedColumn,
  setClickedColumn,
  handleGetBookings,
  handleGetFlights,
}, ref) => {
  const dispatch = useDispatch();
  const bookingType = useSelector(selectBookingType);
  const usedTheme = useSelector(selectUsedTheme);
  const iata = useSelector(selectIata)
  // local modal state
  const [confirmChangeModal, setConfirmChangeModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEmployeeSettingsModal, setShowEmployeeSettingsModal] = useState(false);

  // modal-specific selectors (isolated from main timeline render)
  const sliceShowConfirmationModal = useSelector(selectShowConfirmationModal);
  const viewTask = useSelector(selectViewTask);
  const selectedTaskToView = useSelector(selectSelectedTaskToView);
  const showEditModal = useSelector(selectShowEditModal);
  const modalType = useSelector(selectEditModalType);
  const paxCheckInModal = useSelector(selectPaxCheckInModal);
  const paxLocation = useSelector(selectPaxLocation);
  const ShowDeleteBookingModal = useSelector(selectShowDeleteBookingModal);
  const showBreakTaskModal = useSelector(selectShowBreakTaskModal);
  const showAdhocTaskModal = useSelector(selectShowAdhocTaskModal);
  const selectedAdhocTask = useSelector(selectSelectedAdhocTask);
  const showFlightList = useSelector(selectShowFlightList);
  const flightModal = useSelector(selectFlightModal);
  const passengerModal = useSelector(selectPassengerModal);
  const selectedForChat = useSelector(selectSelectedForChat);
  const openBroadcastMessages = useSelector(selectOpenBroadcastMessages);
  const selectedFlightToChat = useSelector(selectSelectedFlightToChat);
  const changeLocModal = useSelector(selectChangeLocModal);
  const showToast = useSelector(selectShowToast);
  const internalNotify = useSelector(selectInternalNotify);
  const selectedTimeline = useSelector(selectSelectedTimeline);
  // context menu selectors
  const showMenuContext = useSelector(selectMenuContext);
  const points = useSelector(selectMenuPoints);
  const flightContext = useSelector(selectFlightContext);
  const flightContextPoints = useSelector(selectFlightContextPoints);
  const paxMenuContextModal = useSelector(selectPaxMenuContextModal);
  const passenger = useSelector(selectPaxMenuContext);
  const menuLocationPoints = useSelector(selectPaxMenuLocationPoints);
  //
  const showAddPassenger = useSelector(selectShowAddPassenger);
  const additionalPassengers = useSelector(selectAdditionalPassengers);
  const addFlightPassengerStatus = useSelector(selectAddFlightPassengerStatus);
  const selectedFlightToView = useSelector(selectSelectedFlightToView);
  const globalAddPassengerFlight = showAddPassenger === selectedFlightToView?.bookingKey
    ? selectedFlightToView
    : null;
  // expose modal openers to parent via ref
  useImperativeHandle(ref, () => ({
    openCreateModal: () => setShowCreateModal(true),
    openEmployeeSettings: () => setShowEmployeeSettingsModal(true),
  }), []);

  // sync confirmation modal with Redux
  React.useEffect(() => {
    if (sliceShowConfirmationModal) setConfirmChangeModal(true);
  }, [sliceShowConfirmationModal]);

  // close handlers
  const closeConfirmationModal = useCallback(() => {
    setConfirmChangeModal(false);
    dispatch(resetSelectedTask());
  }, [dispatch]);

  const cancelTask = useCallback(() => {
    setConfirmChangeModal(false);
    dispatch(resetSelectedTask());
    dispatch(resetTimelineToCache());
  }, [dispatch]);

  const closeCreateBookingModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const closeEditBookingModal = useCallback(() => {
    dispatch(setShowEditTaskModal(false));
  }, [dispatch]);

  const closeViewTaskModal = useCallback(() => {
    dispatch(setViewTask(false));
  }, [dispatch]);

  const closePaxCheckInModal = useCallback(() => {
    dispatch(openPaxCheckInModal(false));
  }, [dispatch]);

  const closeDeleteBookingModal = useCallback(() => {
    dispatch(resetAllMenuContext());
  }, [dispatch]);

  const closeBreakTaskModal = useCallback(() => {
    dispatch(resetBreakTaskFlags());
  }, [dispatch]);

  const closeAdhocTaskModal = useCallback(() => {
    dispatch(resetAdhocTaskFlags());
  }, [dispatch]);

  const closeFlightListModal = useCallback(() => {
    dispatch(setShowFlightList(false));
  }, [dispatch]);

  const closeViewFlightModal = useCallback(() => {
    dispatch(setSelectedFlightToView(null));
    dispatch(resetPassengerFlightList());
    dispatch(setPassengerModal(false));
    dispatch(setSelectedViewPassenger(null));
    dispatch(setFlightModal(false));
    dispatch(resetAllMenuContext());
  }, [dispatch]);

  const closeViewPassengerModal = useCallback(() => {
    dispatch(setPassengerModal(false));
    dispatch(setSelectedViewPassenger(null));
    dispatch(resetAllMenuContext());
  }, [dispatch]);

  const closeSelectedForChatModal = useCallback(() => {
    dispatch(setSelectedForChat(null));
    dispatch(setEmployeeMessages([]));
  }, [dispatch]);

  const closeSelectedFlightToChatModal = useCallback(() => {
    dispatch(setSelectedFlightToChat(null));
  }, [dispatch]);

  // close broadcast messages modal
  const closeBroadcastMessagesModal = useCallback(() => {
    dispatch(setOpenBroadcastMessages(false));
  }, [dispatch]);

  const closeChangeLocModal = useCallback(() => {
    dispatch(setChangeLocModal({ passenger: {}, show: false }));
  }, [dispatch]);

  React.useEffect(() => {
    if (!globalAddPassengerFlight) return;

    if (addFlightPassengerStatus === 'succeeded') {
      closeGlobalAddPassengerModal();
      handleGetBookings(bookingType);
    }
  }, [addFlightPassengerStatus, globalAddPassengerFlight]);

  const closeGlobalAddPassengerModal = () => {
    dispatch(setShowAddPassenger(null));
    dispatch(resetAdditionalPassengers());
    dispatch(resetAdditionalPassengersFormFlags());
    dispatch(resetAddFlightPassenger());
  };

  const createBookingDocumentForSelectedFlight = (passenger, departureDate = null) => {
    const tempFlightNumber = globalAddPassengerFlight?.flightNumber;
    const { carrierCode, number } = formatCarrierCodeAndFlightNumber(tempFlightNumber);
    const flightDate = globalAddPassengerFlight?.scheduledDate || globalAddPassengerFlight?.flightDate || globalAddPassengerFlight?.date;
    const passengerName = [passenger?.firstName, passenger?.secondName].filter(Boolean).join(' ').trim();

    return {
      name: passengerName || "PLACEHOLDER",
      flightDetails: {
        carrierCode: carrierCode?.toUpperCase(),
        flightNumber: number,
        ...(globalAddPassengerFlight?.type === "arrival"
          ? { arrivalDate: flightDate, ...(departureDate && { departureDate }) }
          : { departureDate: flightDate }),
      },
            assistance: Array.isArray(passenger?.assistance) && passenger.assistance.length > 0 ? passenger.assistance : ["PLACEHOLDER"],
            source: {
                source: "PORTAL",
                data: {},
                ...(departureDate && { departureDate }),
            },
            ambiliftRequired: passenger?.ambiliftRequired ?? false
        };
    };

  const addBookingToSelectedFlight = (additionalBoard, departureDate = null) => {
    const bookings = additionalPassengers.map((passenger) =>
      createBookingDocumentForSelectedFlight(passenger, departureDate)
    );

    dispatch(addFlightPassenger({
      bookings,
      iata: iata
    }));
  };

  const handleCloseNotification = useCallback(() => {
    if (internalNotify.flightId) {
      dispatch(markInternalNotifySeen(internalNotify.flightId));
    } else if (internalNotify.id) {
      dispatch(markInternalNotifySeen(internalNotify.id));
    }
    dispatch(resetInternalNotify());
  }, [dispatch, internalNotify]);



  return (
    <>
      {/* job confirmation modal */}
      <ActionsModal
        show={confirmChangeModal}
        handleClose={closeConfirmationModal}
        title="Create Task"
        subtitle={"To create a task for an employee, select the PRM booking that you want to assign, set a pickup and drop off location and add notes if required."}
        isTimeline={true}
      >
        {confirmChangeModal && <JobConfirmation
          taskConfirmed={() => {
            setConfirmChangeModal(false);
            dispatch(resetSelectedTask());
          }}
          taskRejected={cancelTask}
          clickedColumn={clickedColumn}
          setClickedColumn={setClickedColumn}
        />}
      </ActionsModal>

      {/* create booking Modal */}
      <ActionsModal show={showCreateModal} handleClose={closeCreateBookingModal} title="Create Task" minWidth="1100px" maxWidth="1100px" isTimeline={true}>
        {showCreateModal && <AddTaskView onClose={closeCreateBookingModal} handleGetBookings={handleGetBookings} />}
      </ActionsModal>

      {/* edit booking Modal */}
      <ActionsModal show={showEditModal} handleClose={closeEditBookingModal} title={modalType === "BOOKING" ? "Edit Booking" : "Edit Task"} subtitle={"Edit Passenger Information"} minWidth="500px" maxWidth="500px" isTimeline={true}>
        {showEditModal && <EditTimelineBooking onClose={closeEditBookingModal} handleGetBookings={handleGetBookings} />}
      </ActionsModal>

      {/* menu context */}
      {showMenuContext && (
        <MenuContextContainer style={{ position: "absolute", left: points.x, top: points.y }}>
          <JobActionMenu />
        </MenuContextContainer>
      )}

      {/* Flights context */}
      {flightContext && (
        <MenuContextContainer style={{ position: "absolute", left: flightContextPoints?.x, top: flightContextPoints?.y }}>
          <FlightActionMenu />
        </MenuContextContainer>
      )}

      {paxMenuContextModal && (
        <MenuContextContainer style={{ position: "absolute", left: menuLocationPoints.x, top: menuLocationPoints.y }}>
          <LocationContextMenu passenger={passenger} />
        </MenuContextContainer>
      )}

      {/* view task Modal */}
      <ActionsModal
        show={viewTask}
        handleClose={closeViewTaskModal}
        title={selectedTaskToView?.isGateRelocation ? "Gate Relocation" : "Task Information"}
        subtitle={"View information on the selected task, employee and the task"}
        isTimeline={true}
      >
        {viewTask && <ViewJob onClose={closeViewTaskModal} onUpdate={() => handleGetFlights(bookingType)} />}
      </ActionsModal>

      {/* passenger check in Modal */}
      <ActionsModal show={paxCheckInModal}
        handleClose={closePaxCheckInModal}
        title={`${paxLocation ? 'Set Passenger Location' : 'Passenger Check in'}`}
        subtitle={paxLocation ? "Set the passenger location" : "Select the check in location, confirm the assistance required and add any notes if required."}
        isTimeline={true}
      >
        {paxCheckInModal && <EditTimelineBooking
          onClose={closePaxCheckInModal}
          locationSelectTitle={paxLocation ? "Passenger Location" : "Check in Location (Optional)"}
          btnTitle={paxLocation ? "Set Location" : "Check In"}
          placeholder={paxLocation ? "Choose Pax Location..." : "Choose Check in Location..."}
          forCheckIn={true} />}
      </ActionsModal>

      {/* delete booking Modal */}
      <ActionsModal
        show={ShowDeleteBookingModal}
        handleClose={closeDeleteBookingModal}
        title="Delete PRM Booking"
        subtitle={"To delete a PRM Booking, please click the 'Delete PRM Booking' button below. This action cannot be undone."}
        isTimeline={true}
      >
        {ShowDeleteBookingModal && <DeleteBooking onClose={closeDeleteBookingModal} />}
      </ActionsModal>

      {/* break task Modal */}
      <ActionsModal show={showBreakTaskModal} handleClose={closeBreakTaskModal} title="" minWidth="500px" maxWidth="500px" isTimeline={true}>
        {showBreakTaskModal && <CreateBreakTask onClose={closeBreakTaskModal} />}
      </ActionsModal>

      {/* Adhoc Task Modal */}
      <ActionsModal show={showAdhocTaskModal} handleClose={closeAdhocTaskModal} title={!Object.isObjectEmpty(selectedAdhocTask) ? "Edit Adhoc Task" : "Create Adhoc Task"} minWidth="500px" maxWidth="500px" isTimeline={true}>
        {showAdhocTaskModal && <CreateAdhocTask onClose={closeAdhocTaskModal} />}
      </ActionsModal>

      {/* Flight List Modal */}
      <ActionsModal show={showFlightList} handleClose={closeFlightListModal} title="Flight List" subtitle="Select the passenger you are collecting from the list of passengers on the flight" minWidth="445px" maxWidth="445px" isTimeline={true}>
        {showFlightList && <FlightList onClose={closeFlightListModal} />}
      </ActionsModal>

      {/* Show Flight Modal */}
      <ActionsModal show={flightModal} handleClose={closeViewFlightModal} title={``} minWidth="445px" maxWidth="445px" isTimeline={true}>
        {flightModal && <ViewFlight onClose={closeViewFlightModal} onUpdate={() => handleGetFlights(bookingType)} handleGetBookings={handleGetBookings} />}
      </ActionsModal>

      {/* Show Passenger Modal */}
      <ActionsModal show={passengerModal} handleClose={closeViewPassengerModal} title={``} minWidth="445px" maxWidth="445px" isTimeline={true}>
        {passengerModal && <ViewPassenger onClose={closeViewPassengerModal} />}
      </ActionsModal>

      {/* employee chat modal */}
      <ActionsModal show={!Object.isObjectEmpty(selectedForChat)} handleClose={closeSelectedForChatModal} title={`${selectedForChat?.employee?.title} Chat`} minWidth="445px" maxWidth="445px" isTimeline={true}>
        {!Object.isObjectEmpty(selectedForChat) && <EmployeeChat />}
      </ActionsModal>

      {/* flight chat modal */}
      <ActionsModal show={!Object.isObjectEmpty(selectedFlightToChat)} handleClose={closeSelectedFlightToChatModal} title={`${selectedFlightToChat?.flightNumber} Chat`} minWidth="445px" maxWidth="445px" isTimeline={true}>
        {!Object.isObjectEmpty(selectedFlightToChat) && <FlightChat />}
      </ActionsModal>

      {/* broadcast messages modal */}
      <ActionsModal show={openBroadcastMessages} handleClose={closeBroadcastMessagesModal} title="Broadcast Messages" minWidth="445px" maxWidth="445px" isTimeline={true}
        subtitle={selectedTimeline?._id ? `Your in ${selectedTimeline?.title} Area all this Area online employees will be available for the Broadcast Message` : "Your in All Airport View all the online employees will be available for the Broadcast Message"}>
        {openBroadcastMessages && <BroadcastMessage />}
      </ActionsModal>

      {/* employee settings modal */}
      <ActionsModal
        isTimeline={true}
        show={showEmployeeSettingsModal}
        handleClose={() => setShowEmployeeSettingsModal(false)}
        title="Employee Settings"
        subtitle="Manage employee accounts and assignments"
        minWidth="1200px"
        maxWidth="90vw"
      >
        {showEmployeeSettingsModal && <EmployeeSettings />}
      </ActionsModal>

      <ActionsModal
        isTimeline={true}
        show={changeLocModal?.show}
        handleClose={closeChangeLocModal}
        title="Change PRM Passenger Location"
        subtitle={"Please select a new location for the PRM passenger by clicking on the drop down list under 'New Location'. Once selected, you can click 'Update Location' to change the PRM passenger's location in the airport "}
      >
        {changeLocModal?.show && <UpdatePaxLocationModal passenger={changeLocModal?.passenger} showRemoveBtn={false} />}
      </ActionsModal>

      <ActionsModal
        show={!!globalAddPassengerFlight}
        handleClose={closeGlobalAddPassengerModal}
        title={`Add PRM Passenger to ${globalAddPassengerFlight?.flightNumber || ''}`}
        subtitle={globalAddPassengerFlight ? `Please fill in the details of the additional passenger you want to add to flight ${globalAddPassengerFlight?.flightNumber} and click the 'Add Passenger' button.` : ""}
        isTimeline={true}
      >
        {/* inline: this already sits inside a modal */}
        {globalAddPassengerFlight && <PossibleDuplicateWarning inline />}
        {globalAddPassengerFlight && (
          <AdditionalPassengerForm
            type={globalAddPassengerFlight?.type}
            addBooking={addBookingToSelectedFlight}
            flight={globalAddPassengerFlight?.bookingKey}
            handleClose={closeGlobalAddPassengerModal}
            loading={addFlightPassengerStatus === 'loading'}
          />
        )}
      </ActionsModal>

      {/* toast */}
      {showToast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999 }}>
          <CustomToast />
        </div>
      )}
      {/* Internal Notify */}
      {internalNotify && (
        <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 9999 }}>
          <InternalNofification
            title={internalNotify.title}
            body={internalNotify.body}
            onClose={handleCloseNotification}
          />
        </div>
      )}


    </>
  );
}));

export default TimelineModals;
